import cn from 'classnames';
import { useState } from 'react';

import { usePdfHelpers } from 'src/hooks/usePdfHelpers';

import { formatDate } from 'src/utils/formatDate';

import { ImageData, LinkData } from 'src/types/components';

import ArrowForward from 'assets/icons/arrow-forward.svg';
import ExternalIcon from 'assets/icons/external-link.svg';
import PdfIcon from 'assets/icons/pdf.svg';

import PatternBackground from 'components/BaseHelpers/PatternBackground';

import Image from '../Image';
import Link from '../Link';
import RichText from '../RichText';
import Text from '../Text';

import styles from './ArticleCard.module.scss';

export type ArticleCardProps = {
  id?: number | string;
  contentType: string;
  date: string;
  displayDate: string;
  title: string;
  link: LinkData;
  image?: ImageData;
  className?: string;
  excerpt?: string;
};

const ArticleCard = (props: ArticleCardProps): JSX.Element => {
  const { contentType, date, displayDate, title, link, image, className, excerpt } = props;

  const { isPdf } = usePdfHelpers();

  const [hasError, setHasError] = useState(false);

  const dateText = displayDate || formatDate(date);

  if (isPdf) {
    const text = [`"${title}"`, contentType, dateText].filter(Boolean).join(', ');
    return <Link href={link.href}>{text}</Link>;
  }

  return (
    <Link
      className={cn(styles.main, className)}
      href={link.href}
      openNewTab={link.target === '_blank'}
    >
      <div className={styles.top}>
        <Text
          tag="span"
          className={cn('body-eyebrow', styles['content-type'])}
          field={contentType}
        />
        <Text tag="span" className={cn('body-sm', styles.date)} field={dateText} />
      </div>
      <Text tag="span" className={cn('heading-h4', styles.title)} field={title} />
      <RichText className={styles.excerpt} field={excerpt} noDefaultBodyStyle={true} />
      <div className={styles['image-container']}>
        {image?.src && !hasError ? (
          <div className={styles['image-wrapper']}>
            <Image
              className={styles.image}
              {...image}
              fill
              onError={() => {
                setHasError(true);
              }}
            />
          </div>
        ) : (
          <PatternBackground className={styles.pattern} size="small" theme="dark" />
        )}
      </div>
      {link.target === '_blank' && (
        <div className={styles['icon']}>
          {link.href.includes('.pdf') ? (
            <PdfIcon className={styles['pdf-icon']} />
          ) : (
            <ExternalIcon className={styles['external-icon']} />
          )}
        </div>
      )}
      <ArrowForward className={styles.arrow} />
    </Link>
  );
};

export default ArticleCard;
